import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useToast } from '@veracity/vui'
import { useNavigate } from 'react-router-dom'

import { otherBessInputsLibraryClient } from '@/api'
import { config } from '@/config'
import { UpdateOtherBessInputsDatasetRequest } from '@/interfaces'

const BASE_ROUTE = config.links.library.otherBessInputs

export const useOtherBessInputDatasetDetails = (datasetId: number) => {
  const { showSuccess } = useToast()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const QUERY_KEY = [...otherBessInputsLibraryClient.QUERY_KEY, datasetId]

  const getOtherBessInputDatasetQuery = useQuery({
    queryKey: QUERY_KEY,
    queryFn: () => otherBessInputsLibraryClient.getDetails(datasetId)
  })

  const cloneDatasetMutation = useMutation({
    mutationFn: (datasetName: string) =>
      otherBessInputsLibraryClient.cloneDataset(datasetId, {
        name: datasetName
      }),
    onSuccess: clonedDatasetId => {
      showSuccess('Dataset successfully cloned')
      navigate(`${BASE_ROUTE}/${clonedDatasetId}`)
    }
  })

  const removeDatasetMutation = useMutation({
    mutationFn: () => otherBessInputsLibraryClient.removeDataset(datasetId),
    onSuccess: () => {
      showSuccess('Dataset successfully removed')
      navigate(BASE_ROUTE)
    }
  })

  const lockDatasetMutation = useMutation({
    mutationFn: () => otherBessInputsLibraryClient.lockDataset(datasetId),
    onSuccess: async () => {
      showSuccess('Dataset successfully locked')
      await queryClient.invalidateQueries({ queryKey: QUERY_KEY })
    }
  })

  const archiveDatasetMutation = useMutation({
    mutationFn: () => otherBessInputsLibraryClient.archiveDataset(datasetId),
    onSuccess: async () => {
      showSuccess('Dataset successfully archived')
      await queryClient.invalidateQueries({ queryKey: QUERY_KEY })
    }
  })

  const unArchiveDatasetMutation = useMutation({
    mutationFn: () => otherBessInputsLibraryClient.unArchiveDataset(datasetId),
    onSuccess: async () => {
      showSuccess('Dataset successfully unarchived')
      await queryClient.invalidateQueries({ queryKey: QUERY_KEY })
    }
  })

  const updateDatasetMutation = useMutation({
    mutationFn: (req: UpdateOtherBessInputsDatasetRequest) =>
      otherBessInputsLibraryClient.updateDataset(datasetId, req),
    onSuccess: async () => {
      showSuccess('Dataset successfully updated')
      await queryClient.invalidateQueries({ queryKey: QUERY_KEY })
    }
  })

  return {
    datasetInfo: getOtherBessInputDatasetQuery.data,
    isLoadingDatasetInfo: getOtherBessInputDatasetQuery.isFetching,
    cloneDataset: cloneDatasetMutation.mutate,
    isCloneDatasetInProgress: cloneDatasetMutation.isPending,
    removeDataset: removeDatasetMutation.mutate,
    lockDataset: lockDatasetMutation.mutate,
    archiveDataset: archiveDatasetMutation.mutate,
    unArchiveDataset: unArchiveDatasetMutation.mutate,
    getDownloadFileUrl: (fileId?: number) =>
      fileId
        ? otherBessInputsLibraryClient.getDownloadFileUrl(fileId)
        : undefined,
    updateDataset: updateDatasetMutation.mutate,
    isUpdateDatasetInProgress: updateDatasetMutation.isPending
  }
}
